import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { getContext } from '@/context';
import { PRESET_LIKES_URL } from '@/consts/urls';
import getAuthJsonHeaders from '@/requests/common/getAuthJsonHeaders';

import type { PresetLike } from '../types';

interface LikesFetchParams {
  path: string;
  method?: 'PUT' | 'DELETE';
}

/**
 * The gateway derives the service's required `user-id` header from the session,
 * so the SDK fetch wrapper is the only way to reach these endpoints.
 */
const likesFetch = async <TResponse>({
  path,
  method,
}: LikesFetchParams): Promise<TResponse> => {
  const response = await getContext().utils.api.fetch(
    `${PRESET_LIKES_URL}${path}`,
    { method, headers: getAuthJsonHeaders() }
  );

  if (!response.ok) {
    const error = new Error(
      `preset-likes: ${method} ${path} failed (${response.status})`
    );
    errorLogger.trigger(error, BUGSNAG_STATUSES.WARN);
    throw error;
  }

  return (await response.json()) as TResponse;
};

/**
 * Records the like globally. Nothing reads the tally back — the number is not
 * shown — but the service stays the system of record for it, so counts can be
 * turned on later against real history rather than an empty table.
 */
export const togglePresetLike = ({
  documentId,
  isLiked,
}: {
  documentId: string;
  isLiked: boolean;
}): Promise<PresetLike> =>
  likesFetch({ path: `/${documentId}`, method: isLiked ? 'PUT' : 'DELETE' });
